class BlogListingPage extends HTMLElement {
  constructor() {
    super();

    this.state = {
      currentPage: Number(this.dataset.currentPage || 1),
      totalPages: Number(this.dataset.totalPages || 1),
      totalResults: Number(this.dataset.totalResults || 0),
      pageSize: Number(this.dataset.pageSize || 10),
      visibleArticleCount: 0,
      isLoading: false,
      isAutoLoading: false,
    };

    this.abortController = null;
    this.searchDebounce = null;
    this.initialNextUrl = '';
    this.initialCurrentPage = this.state.currentPage;
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    if (this.abortController) this.abortController.abort();
    window.removeEventListener('keydown', this.handleKeydown);
    this.revealStatefulContent();
  }

  init() {
    if (this.normalizePaginatedURL()) return;

    this.cacheDOM();
    this.closeDrawer();
    this.sortFilterSidebar();
    this.syncInitialAccordionState();
    this.markInitialCards();
    this.initialNextUrl = this.loadMoreButton?.dataset.nextUrl || '';
    this.bindEvents();
    this.restoreStateFromURL();
    this.syncParentCheckboxes();
    this.applyFiltersAndSearch({ updateUrl: false });
    this.revealStatefulContent();
  }

  cacheDOM() {
    this.grid = this.querySelector('[data-card-grid]');
    this.searchInput = this.querySelector('[data-search-input]');
    this.checkboxes = Array.from(this.querySelectorAll('[data-filter-checkbox]'));
    this.yearCheckboxes = Array.from(this.querySelectorAll('[data-year-filter]'));
    this.monthCheckboxes = Array.from(this.querySelectorAll('[data-month-filter]'));
    this.yearParent = this.querySelector('[data-year-parent]');
    this.monthParent = this.querySelector('[data-month-parent]');
    this.filterGroupParents = Array.from(this.querySelectorAll('[data-filter-group-parent]'));
    this.openDrawerButtons = Array.from(this.querySelectorAll('[data-open-drawer]'));
    this.closeDrawerButtons = Array.from(this.querySelectorAll('[data-close-drawer]'));
    this.applyButton = this.querySelector('.blog-listing-page__apply');
    this.resetButton = this.querySelector('[data-reset]');
    this.loadMoreButton = this.querySelector('[data-load-more]');
    this.loadMoreWrap = this.querySelector('[data-load-more-wrap]');
    this.emptyState = this.querySelector('[data-empty-state]');
    this.headerCountEls = Array.from(this.querySelectorAll('[data-total-count]'));
    this.loadedCountEls = Array.from(this.querySelectorAll('[data-loaded-count]'));
    this.totalResultEls = Array.from(this.querySelectorAll('[data-total-results]'));
    this.accordions = Array.from(this.querySelectorAll('[data-accordion]'));
  }

  sortFilterSidebar() {
    this.sortChildren(this.querySelector('[data-filter-list]'), '[data-filter-root]');

    this.querySelectorAll('[data-child-filter-list]').forEach((list) => {
      this.sortChildren(list, '[data-child-filter-option]');
    });
  }

  sortChildren(container, selector) {
    if (!container) return;

    const sortedItems = Array.from(container.children)
      .filter((child) => child.matches(selector))
      .map((child, index) => ({ child, index }))
      .sort((a, b) => {
        const orderA = this.getSortOrder(a.child);
        const orderB = this.getSortOrder(b.child);

        if (orderA !== orderB) return orderA - orderB;

        const titleA = a.child.dataset.title || '';
        const titleB = b.child.dataset.title || '';
        const titleComparison = titleA.localeCompare(titleB);

        if (titleComparison !== 0) return titleComparison;

        return a.index - b.index;
      });

    sortedItems.forEach(({ child }) => container.appendChild(child));
  }

  getSortOrder(element) {
    const order = Number(element.dataset.order);
    return Number.isFinite(order) && order > 0 ? order : Number.MAX_SAFE_INTEGER;
  }

  normalizePaginatedURL() {
    const params = new URLSearchParams(window.location.search);
    const page = Number(params.get('page') || 1);

    if (!params.has('page')) return false;

    params.delete('page');
    const query = params.toString();
    const normalizedUrl = `${window.location.pathname}${query ? `?${query}` : ''}`;

    if (page > 1) {
      window.location.replace(normalizedUrl);
      return true;
    }

    history.replaceState({}, '', normalizedUrl);
    return false;
  }

  revealStatefulContent() {
    document.documentElement.classList.remove('blog-listing-page-state-pending');
  }

  bindEvents() {
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleAccordionClick = this.handleAccordionClick.bind(this);

    this.searchInput?.addEventListener('input', () => {
      window.clearTimeout(this.searchDebounce);
      this.searchDebounce = window.setTimeout(() => {
        this.applyFiltersAndSearch();
        this.updateResetState();
      }, 150);
    });

    this.checkboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', (event) => {
        if (event.currentTarget.hasAttribute('data-parent-filter')) {
          this.handleParentCheckbox(event.currentTarget);
        } else {
          this.syncParentCheckboxes();
        }

        this.applyFiltersAndSearch();
        this.updateResetState();
      });
    });

    this.yearCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', () => {
        this.applyFiltersAndSearch();
        this.updateResetState();
      });
    });

    this.monthCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', () => {
        this.applyFiltersAndSearch();
        this.updateResetState();
      });
    });

    [this.yearParent, this.monthParent, ...this.filterGroupParents].forEach((parent) => {
      if (!parent) return;
      const parentDetails = parent.closest('details');
      if (!parentDetails) return;

      parent.checked = parentDetails.open;

      parentDetails.addEventListener('toggle', () => {
        parent.checked = parentDetails.open;
      });

      parent.addEventListener('change', () => {
        parent.checked = parentDetails.open;
      });
    });

    this.applyButton?.addEventListener('click', () => {
      this.applyFiltersAndSearch();
      this.closeDrawer();
    });

    this.resetButton?.addEventListener('click', () => this.resetAll());
    this.loadMoreButton?.addEventListener('click', () => this.loadMore());
    this.openDrawerButtons.forEach((button) => button.addEventListener('click', () => this.openDrawer()));
    this.closeDrawerButtons.forEach((button) => button.addEventListener('click', () => this.closeDrawer()));

    this.addEventListener('click', this.handleAccordionClick, true);

    window.addEventListener('keydown', this.handleKeydown);
  }

  restoreStateFromURL() {
    const params = new URLSearchParams(window.location.search);
    const filters = this.parseListParam(params.get('filters'));
    const years = this.parseListParam(params.get('years'));
    const months = this.parseListParam(params.get('months'));
    const search = params.get('search') || '';

    this.state.currentPage = Number(this.dataset.currentPage || 1);

    if (this.searchInput) this.searchInput.value = search;

    this.checkboxes.forEach((checkbox) => {
      checkbox.checked = filters.includes(checkbox.value);
    });

    this.checkboxes
      .filter((checkbox) => checkbox.checked && checkbox.hasAttribute('data-parent-filter'))
      .forEach((checkbox) => this.handleParentCheckbox(checkbox));

    this.yearCheckboxes.forEach((checkbox) => {
      checkbox.checked = years.includes(checkbox.value);
    });

    this.monthCheckboxes.forEach((checkbox) => {
      checkbox.checked = months.includes(checkbox.value);
    });
  }

  updateURL() {
    const params = new URLSearchParams(window.location.search);
    const activeFilters = this.getActiveFilters();
    const activeYears = this.getActiveYears();
    const activeMonths = this.getActiveMonths();
    const searchTerm = this.getSearchTerm();

    if (activeFilters.length) {
      params.set('filters', activeFilters.join(','));
    } else {
      params.delete('filters');
    }

    if (activeYears.length) {
      params.set('years', activeYears.join(','));
    } else {
      params.delete('years');
    }

    if (activeMonths.length) {
      params.set('months', activeMonths.join(','));
    } else {
      params.delete('months');
    }

    if (searchTerm) {
      params.set('search', searchTerm);
    } else {
      params.delete('search');
    }

    params.delete('page');
    params.delete('date');

    const query = params.toString();
    const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}`;
    history.replaceState({}, '', nextUrl);
  }

  getActiveFilters() {
    return this.checkboxes.filter((checkbox) => checkbox.checked).map((checkbox) => checkbox.value);
  }

  getActiveYears() {
    return this.yearCheckboxes.filter((checkbox) => checkbox.checked).map((checkbox) => checkbox.value);
  }

  getActiveMonths() {
    return this.monthCheckboxes.filter((checkbox) => checkbox.checked).map((checkbox) => checkbox.value);
  }

  getSearchTerm() {
    return (this.searchInput?.value || '').trim().toLowerCase();
  }

  getCards() {
    return Array.from(this.grid?.querySelectorAll('[data-blog-card]') || []);
  }

  markInitialCards() {
    this.getCards().forEach((card) => {
      card.dataset.initialCard = 'true';
    });
  }

  filterCards(cards, activeFilters) {
    if (!activeFilters.length) return new Set(cards);

    return new Set(
      cards.filter((card) => {
        const cardFilters = (card.dataset.filters || '').split(/\s+/).filter(Boolean);
        return activeFilters.some((filter) => cardFilters.includes(filter));
      }),
    );
  }

  filterCardsByYear(cards, activeYears) {
    if (!activeYears.length) return new Set(cards);

    return new Set(
      cards.filter((card) => {
        const year = card.dataset.year;
        if (!year) return false;
        return activeYears.includes(year);
      }),
    );
  }

  filterCardsByMonth(cards, activeMonths) {
    if (!activeMonths.length) return new Set(cards);

    return new Set(
      cards.filter((card) => {
        const month = card.dataset.month;
        if (!month) return false;
        return activeMonths.includes(month);
      }),
    );
  }

  searchCards(cards, searchTerm) {
    if (!searchTerm) return new Set(cards);

    return new Set(
      cards.filter((card) => {
        const searchableText = card.dataset.search || '';
        return searchableText.includes(searchTerm);
      }),
    );
  }

  async applyFiltersAndSearch(options = {}) {
    this.runFilterPipeline(options);

    if (this.state.isAutoLoading) return;
    if (!this.hasActiveFilter()) return;
    if (!this.loadMoreButton?.dataset.nextUrl) return;

    this.state.isAutoLoading = true;
    try {
      while (this.hasActiveFilter() && this.loadMoreButton?.dataset.nextUrl) {
        const ok = await this.fetchNextPage({ silent: true });
        if (!ok) break;
        this.runFilterPipeline({ updateUrl: false });
      }
    } finally {
      this.state.isAutoLoading = false;
      this.updateLoadMoreVisibility();
    }
  }

  runFilterPipeline(options = {}) {
    const { updateUrl = true } = options;
    const cards = this.getCards();
    const activeFilters = this.getActiveFilters();
    const activeYears = this.getActiveYears();
    const activeMonths = this.getActiveMonths();
    const searchTerm = this.getSearchTerm();
    const filterMatches = this.filterCards(cards, activeFilters);
    const yearMatches = this.filterCardsByYear(cards, activeYears);
    const monthMatches = this.filterCardsByMonth(cards, activeMonths);
    const searchMatches = this.searchCards(cards, searchTerm);
    const hasActiveState = this.hasActiveFilter();
    let visibleCount = 0;

    cards.forEach((card) => {
      const isSpecial = card.hasAttribute('data-special-card');
      const isVisible = isSpecial
        ? !hasActiveState
        : filterMatches.has(card) &&
          yearMatches.has(card) &&
          monthMatches.has(card) &&
          searchMatches.has(card);
      card.classList.toggle('is-hidden', !isVisible);
      if (isVisible && card.hasAttribute('data-article-card')) visibleCount += 1;
    });

    if (this.emptyState) {
      this.emptyState.hidden = visibleCount > 0 || cards.length === 0;
    }

    this.state.visibleArticleCount = visibleCount;
    this.updateCounts(visibleCount);
    this.updateLoadMoreVisibility();
    this.updateResetState();

    if (updateUrl) this.updateURL();
  }

  hasActiveFilter() {
    return Boolean(
      this.getActiveFilters().length ||
        this.getActiveYears().length ||
        this.getActiveMonths().length ||
        this.getSearchTerm(),
    );
  }

  handleParentCheckbox(parentCheckbox) {
    const parentSlug = parentCheckbox.value;
    const children = this.checkboxes.filter((checkbox) => checkbox.dataset.parentSlug === parentSlug);

    parentCheckbox.indeterminate = false;

    children.forEach((child) => {
      child.checked = parentCheckbox.checked;
    });
  }

  syncParentCheckboxes() {
    const parentCheckboxes = this.checkboxes.filter((checkbox) => checkbox.hasAttribute('data-parent-filter'));

    parentCheckboxes.forEach((parent) => {
      const children = this.checkboxes.filter((checkbox) => checkbox.dataset.parentSlug === parent.value);
      if (!children.length) return;

      const checkedChildren = children.filter((child) => child.checked);
      parent.checked = checkedChildren.length === children.length;
      parent.indeterminate = checkedChildren.length > 0 && checkedChildren.length < children.length;
    });
  }

  async loadMore() {
    const ok = await this.fetchNextPage();
    if (ok) this.applyFiltersAndSearch();
  }

  async fetchNextPage({ silent = false } = {}) {
    if (!this.loadMoreButton || this.state.isLoading) return false;

    const nextUrl = this.loadMoreButton.dataset.nextUrl;
    if (!nextUrl) return false;

    this.state.isLoading = true;
    if (!silent) {
      this.loadMoreButton.classList.add('is-loading');
      this.loadMoreButton.disabled = true;
    }

    try {
      const html = await this.fetchPage(nextUrl);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nextComponent = doc.querySelector('blog-listing-page');

      if (!nextComponent) throw new Error('Blog listing section was not found in fetched page.');

      this.appendCards(nextComponent);
      this.updatePaginationState(nextComponent);
      return true;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      this.state.isLoading = false;
      if (!silent) {
        this.loadMoreButton.classList.remove('is-loading');
        this.loadMoreButton.disabled = false;
      }
      this.updateLoadMoreVisibility();
    }
  }

  async fetchPage(url) {
    if (this.abortController) this.abortController.abort();

    this.abortController = new AbortController();
    const fetchUrl = new URL(url, window.location.origin);
    if (this.dataset.sectionId) {
      fetchUrl.searchParams.set('section_id', this.dataset.sectionId);
    }

    const response = await fetch(fetchUrl.toString(), {
      signal: this.abortController.signal,
      credentials: 'same-origin',
    });

    if (!response.ok) throw new Error(`Unable to load blog page: ${response.status}`);
    return response.text();
  }

  appendCards(nextComponent) {
    const nextCards = Array.from(nextComponent.querySelectorAll('[data-blog-card]'));
    if (!nextCards.length || !this.grid) return;

    const existingArticleIds = new Set(
      this.getCards()
        .map((card) => card.dataset.articleId)
        .filter(Boolean),
    );
    const existingSpecialIds = new Set(
      this.getCards()
        .map((card) => card.dataset.specialId)
        .filter(Boolean),
    );
    const fragment = document.createDocumentFragment();

    nextCards.forEach((card) => {
      const articleId = card.dataset.articleId;
      const specialId = card.dataset.specialId;

      if (articleId && existingArticleIds.has(articleId)) return;
      if (specialId && existingSpecialIds.has(specialId)) return;

      if (articleId) existingArticleIds.add(articleId);
      if (specialId) existingSpecialIds.add(specialId);

      fragment.appendChild(document.importNode(card, true));
    });

    this.grid.appendChild(fragment);
  }

  updatePaginationState(nextComponent) {
    const nextLoadMore = nextComponent.querySelector('[data-load-more]');
    const nextUrl = nextLoadMore?.dataset.nextUrl || '';
    const nextPage = Number(nextComponent.dataset.currentPage || this.state.currentPage + 1);

    this.state.currentPage = Number.isNaN(nextPage) ? this.state.currentPage + 1 : nextPage;
    this.state.totalPages = Number(nextComponent.dataset.totalPages || this.state.totalPages);

    if (this.loadMoreButton) {
      if (nextUrl) {
        this.loadMoreButton.dataset.nextUrl = nextUrl;
      } else {
        delete this.loadMoreButton.dataset.nextUrl;
      }
    }
  }

  resetAll() {
    this.getCards().forEach((card) => {
      if (card.dataset.initialCard !== 'true') card.remove();
    });

    this.checkboxes.forEach((checkbox) => {
      checkbox.checked = false;
      checkbox.indeterminate = false;
    });

    this.yearCheckboxes.forEach((checkbox) => {
      checkbox.checked = false;
    });

    this.monthCheckboxes.forEach((checkbox) => {
      checkbox.checked = false;
    });

    if (this.searchInput) this.searchInput.value = '';

    this.state.currentPage = 1;

    if (this.loadMoreButton) {
      if (this.initialNextUrl) {
        this.loadMoreButton.dataset.nextUrl = this.initialNextUrl;
      } else {
        delete this.loadMoreButton.dataset.nextUrl;
      }
    }

    this.applyFiltersAndSearch();
    this.closeDrawer();
  }

  updateResetState() {
    if (!this.resetButton) return;
    this.resetButton.disabled = !this.hasActiveFilter();
  }

  updateCounts(visibleArticleCount) {
    const loadedArticleCount = this.getCards().filter((card) => card.hasAttribute('data-article-card')).length;
    const hasActiveState = this.hasActiveFilter();
    const headerCount = hasActiveState ? visibleArticleCount : this.state.totalResults;
    const loadedCount = hasActiveState ? visibleArticleCount : loadedArticleCount;

    this.headerCountEls.forEach((element) => {
      element.textContent = String(headerCount);
    });

    this.loadedCountEls.forEach((element) => {
      element.textContent = String(loadedCount);
    });

    this.totalResultEls.forEach((element) => {
      element.textContent = String(this.state.totalResults);
    });
  }

  updateLoadMoreVisibility() {
    if (!this.loadMoreWrap) return;

    const hasNextPage = Boolean(this.loadMoreButton?.dataset.nextUrl);
    const hasActiveState = this.hasActiveFilter();
    const hasEnoughFilteredItems = this.state.visibleArticleCount >= this.state.pageSize;

    const loadedArticleCount = this.getCards().filter((card) => card.hasAttribute('data-article-card')).length;
    const remaining = Math.max(this.state.totalResults - loadedArticleCount, 0);
    const nextLoadCount = Math.min(remaining, this.state.pageSize);

    if (this.loadMoreButton?.children[0]) {
      this.loadMoreButton.children[0].innerHTML = `Show ${nextLoadCount} More Results`;
    }

    this.loadMoreWrap.hidden = !hasNextPage || (hasActiveState && !hasEnoughFilteredItems);
  }

  openDrawer() {
    this.scrollPosition = window.scrollY;
    this.lastFocusedBeforeDrawer = document.activeElement;

    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollPosition}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';

    this.classList.add('is-drawer-open');

    const sidebar = this.querySelector('[data-sidebar]');
    sidebar?.setAttribute('aria-hidden', 'false');
    this.openDrawerButtons.forEach((button) => button.setAttribute('aria-expanded', 'true'));

    const closeButton = this.closeDrawerButtons[0];
    closeButton?.focus();
  }

  closeDrawer() {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';

    window.scrollTo(0, this.scrollPosition || 0);

    const wasOpen = this.classList.contains('is-drawer-open');
    this.classList.remove('is-drawer-open');

    const sidebar = this.querySelector('[data-sidebar]');
    sidebar?.setAttribute('aria-hidden', 'true');
    this.openDrawerButtons.forEach((button) => button.setAttribute('aria-expanded', 'false'));

    if (wasOpen && this.lastFocusedBeforeDrawer && typeof this.lastFocusedBeforeDrawer.focus === 'function') {
      this.lastFocusedBeforeDrawer.focus();
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape') this.closeDrawer();
  }

  handleAccordionClick(event) {
    const summary = event.target.closest('summary[data-accordion-trigger]');
    if (!summary) return;

    const interactive = event.target.closest('input, button, textarea, select, label, a');
    const summaryHasParentCheckbox = summary.querySelector('[data-year-parent], [data-month-parent], [data-filter-group-parent]');
    if (interactive && interactive !== summary && !summaryHasParentCheckbox) return;

    const details = summary.closest('details[data-accordion]');
    if (!details) return;

    const panel = details.querySelector('.blog-listing-page__accordion-panel');
    if (!panel || panel.hasAttribute('data-animating')) return;

    event.preventDefault();

    if (details.hasAttribute('open')) {
      this.animateClose(details, summary, panel);
    } else {
      this.animateOpen(details, summary, panel);
    }
  }

  animateOpen(details, summary, panel) {
    details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');
    panel.setAttribute('data-animating', 'open');

    const targetHeight = panel.scrollHeight;

    panel.style.height = '0px';
    panel.style.paddingTop = '0px';
    panel.style.paddingBottom = '0px';
    panel.offsetHeight;
    
    requestAnimationFrame(() => {
      panel.style.height = `${targetHeight + 12}px`;
      panel.style.paddingTop = '';
      panel.style.paddingBottom = '';
    });

    const onEnd = (event) => {
      if (event.target !== panel || event.propertyName !== 'height') return;
      panel.style.height = '';
      panel.style.paddingTop = '';
      panel.style.paddingBottom = '';
      panel.removeAttribute('data-animating');
      panel.removeEventListener('transitionend', onEnd);
    };

    panel.addEventListener('transitionend', onEnd);
  }

  animateClose(details, summary, panel) {
    summary.setAttribute('aria-expanded', 'false');
    const parentCheckbox = details.querySelector('[data-year-parent], [data-month-parent], [data-filter-group-parent]');
    if (parentCheckbox) parentCheckbox.checked = false;
    panel.setAttribute('data-animating', 'close');

    const computed = window.getComputedStyle(panel);
    panel.style.height = `${panel.scrollHeight}px`;
    panel.style.paddingTop = computed.paddingTop;
    panel.style.paddingBottom = computed.paddingBottom;
    panel.offsetHeight;

    requestAnimationFrame(() => {
      panel.style.height = '0px';
      panel.style.paddingTop = '0px';
      panel.style.paddingBottom = '0px';
    });

    const onEnd = (event) => {
      if (event.target !== panel || event.propertyName !== 'height') return;
      details.removeAttribute('open');
      panel.style.height = '';
      panel.style.paddingTop = '';
      panel.style.paddingBottom = '';
      panel.removeEventListener('transitionend', onEnd);
      panel.removeAttribute('data-animating');
    };

    panel.addEventListener('transitionend', onEnd);
  }

  syncInitialAccordionState() {
    this.accordions.forEach((accordion) => {
      const summary = accordion.querySelector('summary[data-accordion-trigger]');
      if (!summary) return;
      summary.setAttribute('aria-expanded', accordion.hasAttribute('open') ? 'true' : 'false');
    });
  }

  parseListParam(value) {
    if (!value) return [];
    return value
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
  }
}

if (!customElements.get('blog-listing-page')) {
  customElements.define('blog-listing-page', BlogListingPage);
}
